import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { KeyRound, Hash, Check, User } from "lucide-react";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { useChangeOwnPassword, useSetOwnPin } from "@/features/user/hooks";
import { AdminDrawer } from "@/components/admin/AdminDrawer";
import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { PinDisplay, PinPad } from "@/components/auth/PinPad";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import "./PerfilPage.css";

const PIN_LENGTH = 6;

const ROLE_LABELS: Record<string, string> = {
  super: "Admin",
  client: "Cliente",
  checker: "Conferente",
  overviewer: "Visualizador",
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.625rem 0.75rem",
  background: "var(--login-bg)",
  border: "1px solid var(--login-border)",
  borderRadius: "10px",
  color: "var(--login-text)",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.9375rem",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 600,
  fontSize: "0.75rem",
  color: "var(--login-muted)",
  display: "block",
  marginBottom: "0.375rem",
};

// ── Change Password drawer ────────────────────────────────────────────────────

function ChangePasswordDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState("");
  const [saved, setSaved] = useState(false);

  const mutation = useChangeOwnPassword();

  function reset() {
    setCurrent("");
    setNext("");
    setConfirm("");
    setLocalError("");
    setSaved(false);
    mutation.reset();
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError("");

    if (!current) {
      setLocalError("Digite a senha atual.");
      return;
    }
    if (next.length < 4) {
      setLocalError("A nova senha deve ter pelo menos 4 caracteres.");
      return;
    }
    if (next !== confirm) {
      setLocalError("As senhas não coincidem.");
      return;
    }

    try {
      await mutation.mutateAsync({
        current_password: current,
        new_password: next,
      });
      setSaved(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch {
      // displayed via mutation.error
    }
  }

  const errorMsg =
    localError ||
    (mutation.isError
      ? getUserFacingErrorMessage(
          mutation.error,
          "Não foi possível salvar. Tente novamente.",
        )
      : "");

  return (
    <AdminDrawer
      open={open}
      onClose={handleClose}
      title="Alterar Senha"
      backdrop="transparent"
    >
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        <div>
          <label style={labelStyle}>Senha atual</label>
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="Senha atual"
            style={inputStyle}
            onFocus={(e) =>
              (e.target.style.borderColor = "var(--login-accent)")
            }
            onBlur={(e) => (e.target.style.borderColor = "var(--login-border)")}
          />
        </div>

        <div>
          <label style={labelStyle}>Nova senha</label>
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="Mínimo 4 caracteres"
            style={inputStyle}
            onFocus={(e) =>
              (e.target.style.borderColor = "var(--login-accent)")
            }
            onBlur={(e) => (e.target.style.borderColor = "var(--login-border)")}
          />
        </div>

        <div>
          <label style={labelStyle}>Confirmar nova senha</label>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repita a nova senha"
            style={inputStyle}
            onFocus={(e) =>
              (e.target.style.borderColor = "var(--login-accent)")
            }
            onBlur={(e) => (e.target.style.borderColor = "var(--login-border)")}
          />
        </div>

        {errorMsg && (
          <p
            style={{
              fontSize: "0.8125rem",
              color: "var(--login-error, #c44e3a)",
              margin: 0,
            }}
          >
            {errorMsg}
          </p>
        )}

        <button
          type="submit"
          disabled={mutation.isPending || saved}
          style={{
            padding: "0.75rem",
            background: saved
              ? "var(--login-accent)"
              : mutation.isPending
                ? "var(--login-muted)"
                : "var(--login-accent)",
            border: "none",
            borderRadius: "10px",
            color: "var(--profile-on-accent)",
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: "1rem",
            letterSpacing: "0.06em",
            cursor: mutation.isPending || saved ? "not-allowed" : "pointer",
            transition: "background 0.15s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            marginTop: "0.25rem",
          }}
        >
          {saved ? (
            <>
              <Check size={16} strokeWidth={2.5} />
              Salvo!
            </>
          ) : mutation.isPending ? (
            "Salvando..."
          ) : (
            "ALTERAR SENHA"
          )}
        </button>
      </form>
    </AdminDrawer>
  );
}

// ── PIN setup drawer ──────────────────────────────────────────────────────────

function PinDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(false);
  const [localError, setLocalError] = useState("");
  const [saved, setSaved] = useState(false);
  const [mode, setMode] = useState<"set" | "remove">("set");
  const [displayMode, setDisplayMode] = useState<"set" | "remove">("set");
  const [modePhase, setModePhase] = useState<"idle" | "enter" | "exit">("idle");

  const mutation = useSetOwnPin();

  useEffect(() => {
    if (!open) {
      setDisplayMode("set");
      setModePhase("idle");
      return;
    }

    if (mode === displayMode) return;

    setModePhase("exit");
    const timeout = window.setTimeout(() => {
      setDisplayMode(mode);
      setModePhase("enter");
    }, 150);

    return () => window.clearTimeout(timeout);
  }, [displayMode, mode, open]);

  useEffect(() => {
    if (modePhase !== "enter") return;
    const frame = window.requestAnimationFrame(() => setModePhase("idle"));
    return () => window.cancelAnimationFrame(frame);
  }, [modePhase]);

  function reset() {
    setCurrentPassword("");
    setPin("");
    setPinError(false);
    setLocalError("");
    setSaved(false);
    mutation.reset();
    setMode("set");
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleDigit(d: string) {
    if (pin.length < PIN_LENGTH && !mutation.isPending) {
      setPin((p) => p + d);
      setPinError(false);
    }
  }

  function handleBackspace() {
    setPin((p) => p.slice(0, -1));
    setPinError(false);
  }

  const handleConfirm = useCallback(async () => {
    if (mode === "set" && pin.length < PIN_LENGTH) return;
    setLocalError("");

    if (!currentPassword) {
      setLocalError("Digite a senha atual para confirmar.");
      return;
    }

    if (mode === "set" && pin.length !== PIN_LENGTH) {
      setLocalError(`O PIN deve ter ${PIN_LENGTH} dígitos.`);
      return;
    }

    try {
      await mutation.mutateAsync({
        current_password: currentPassword,
        pincode: mode === "set" ? pin : undefined,
      });
      setSaved(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch {
      if (mode === "set") {
        setPinError(true);
        setTimeout(() => {
          setPinError(false);
          setPin("");
        }, 600);
      }
    }
  }, [mode, pin, currentPassword, mutation]);

  const errorMsg =
    localError ||
    (mutation.isError
      ? getUserFacingErrorMessage(
          mutation.error,
          "Não foi possível configurar o PIN. Tente novamente.",
        )
      : "");

  return (
    <AdminDrawer
      open={open}
      onClose={handleClose}
      title="Configurar PIN"
      backdrop="transparent"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        {/* Mode selector */}
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
          }}
        >
          {(["set", "remove"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMode(m);
                setPin("");
                setPinError(false);
                setLocalError("");
                mutation.reset();
              }}
              style={{
                flex: 1,
                padding: "0.5rem 0.75rem",
                background:
                  mode === m ? "var(--login-accent)" : "var(--login-bg)",
                border: `1px solid ${mode === m ? "var(--login-accent)" : "var(--login-border)"}`,
                borderRadius: "10px",
                color:
                  mode === m
                    ? "var(--profile-on-accent)"
                    : "var(--login-muted)",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.875rem",
                fontWeight: mode === m ? 600 : 400,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {m === "set" ? "Definir PIN" : "Remover PIN"}
            </button>
          ))}
        </div>

        {/* Current password */}
        <div>
          <label style={labelStyle}>Senha atual</label>
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Confirmar com senha atual"
            style={inputStyle}
            onFocus={(e) =>
              (e.target.style.borderColor = "var(--login-accent)")
            }
            onBlur={(e) => (e.target.style.borderColor = "var(--login-border)")}
          />
        </div>

        <div
          style={{
            display: "grid",
            gap: "1rem",
            opacity: modePhase === "exit" ? 0 : 1,
            transform:
              modePhase === "exit"
                ? "translateY(-10px) scale(0.985)"
                : modePhase === "enter"
                  ? "translateY(12px) scale(0.985)"
                  : "translateY(0) scale(1)",
            filter: modePhase === "exit" ? "blur(2px)" : "blur(0px)",
            transition:
              "opacity 0.18s ease, transform 0.24s cubic-bezier(0.16, 1, 0.3, 1), filter 0.18s ease",
            pointerEvents: modePhase === "exit" ? "none" : "auto",
            transformOrigin: "top center",
          }}
        >
          {displayMode === "set" ? (
            <>
              <p
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--login-muted)",
                  textAlign: "center",
                  margin: 0,
                }}
              >
                Digite o novo PIN de {PIN_LENGTH} dígitos
              </p>

              <div className={pinError ? "animate-shake" : ""}>
                <PinDisplay
                  length={PIN_LENGTH}
                  filled={pin.length}
                  error={pinError}
                />
              </div>

              <PinPad
                onDigit={handleDigit}
                onBackspace={handleBackspace}
                onConfirm={handleConfirm}
                disabled={mutation.isPending || saved}
                active={open && displayMode === "set" && modePhase !== "exit"}
              />
            </>
          ) : null}
        </div>

        {errorMsg && (
          <p
            style={{
              fontSize: "0.8125rem",
              color: "var(--login-error, #c44e3a)",
              margin: 0,
              textAlign: "center",
            }}
          >
            {errorMsg}
          </p>
        )}

        <button
          type="button"
          disabled={
            mutation.isPending ||
            saved ||
            (mode === "set" && pin.length < PIN_LENGTH)
          }
          onClick={handleConfirm}
          style={{
            padding: "0.75rem",
            background: saved
              ? "var(--login-accent)"
              : mutation.isPending ||
                  (mode === "set" && pin.length < PIN_LENGTH)
                ? "var(--login-muted)"
                : "var(--login-accent)",
            border: "none",
            borderRadius: "10px",
            color: "var(--profile-on-accent)",
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: "1rem",
            letterSpacing: "0.06em",
            cursor:
              mutation.isPending ||
              saved ||
              (mode === "set" && pin.length < PIN_LENGTH)
                ? "not-allowed"
                : "pointer",
            transition: "background 0.15s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          {saved ? (
            <>
              <Check size={16} strokeWidth={2.5} />
              Salvo!
            </>
          ) : mutation.isPending ? (
            "Salvando..."
          ) : mode === "set" ? (
            "DEFINIR PIN"
          ) : (
            "REMOVER PIN"
          )}
        </button>
      </div>
    </AdminDrawer>
  );
}

// ── Action row ────────────────────────────────────────────────────────────────

function ActionRow({
  icon: Icon,
  label,
  description,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        width: "100%",
        padding: "1rem",
        background: "var(--login-surface)",
        border: "1px solid var(--login-border)",
        borderRadius: "0.75rem",
        cursor: "pointer",
        textAlign: "left",
        transition: "border-color 0.15s, background 0.15s",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor =
          "var(--login-accent)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor =
          "var(--login-border)";
      }}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "0.5rem",
          background: "var(--login-bg)",
          border: "1px solid var(--login-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={18} strokeWidth={1.5} color="var(--login-accent)" />
      </div>
      <div>
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.9375rem",
            fontWeight: 500,
            color: "var(--login-text)",
            margin: 0,
          }}
        >
          {label}
        </p>
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.8125rem",
            color: "var(--login-muted)",
            margin: "2px 0 0 0",
          }}
        >
          {description}
        </p>
      </div>
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function PerfilPage() {
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  return (
    <>
      <style>{`
        @keyframes profilePageIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div
        className="perfil-page app-page-frame"
        style={{
          padding: isDesktop
            ? "2rem var(--app-page-gutter)"
            : "1.25rem var(--app-page-gutter)",
          maxWidth: "calc(1120px + 2 * var(--app-page-gutter))",
          width: "100%",
          boxSizing: "border-box",
          margin: "0 auto",
          animation: "profilePageIn 0.22s ease both",
        }}
      >
        <div style={{ marginBottom: "1rem" }}>
          <AdminBreadcrumb
            items={[
              { label: "Mais", onClick: () => navigate({ to: "/mais" }) },
              { label: "Meu Perfil" },
            ]}
          />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "0.85rem",
            marginBottom: "1.25rem",
          }}
        >
          <div
            style={{
              width: "2.75rem",
              height: "2.75rem",
              borderRadius: "1rem",
              background:
                "linear-gradient(180deg, color-mix(in srgb, var(--login-accent) 14%, transparent), color-mix(in srgb, var(--login-accent) 5%, transparent))",
              color: "var(--login-accent)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <User size={18} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                fontSize: isDesktop ? "1.9rem" : "1.65rem",
                letterSpacing: "0.04em",
                color: "var(--login-text)",
              }}
            >
              MEU PERFIL
            </h1>
          </div>
        </div>

        {/* Avatar + info card */}
        <div
          style={{
            background: "var(--login-surface)",
            border: "1px solid var(--login-border)",
            borderRadius: "1rem",
            padding: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "1.25rem",
            marginBottom: "1.5rem",
          }}
        >
          {/* Avatar */}
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "var(--login-border)",
              border: "2px solid var(--login-accent)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'DM Mono', monospace",
              fontSize: "1.375rem",
              fontWeight: 700,
              color: "var(--login-text)",
              flexShrink: 0,
            }}
          >
            {user ? getInitials(user.name) : "—"}
          </div>

          <div style={{ minWidth: 0 }}>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "1.125rem",
                fontWeight: 600,
                color: "var(--login-text)",
                margin: "0 0 2px 0",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.name ?? "—"}
            </p>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.875rem",
                color: "var(--login-muted)",
                margin: "0 0 8px 0",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.email ?? "—"}
            </p>

            {user && (
              <span
                style={{
                  display: "inline-block",
                  padding: "2px 10px",
                  borderRadius: "999px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: "0.04em",
                  background:
                    "color-mix(in srgb, var(--login-accent) 12%, var(--login-surface))",
                  color: "var(--login-text)",
                }}
              >
                {ROLE_LABELS[user.role] ?? user.role}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="perfil-actions">
          <ActionRow
            icon={KeyRound}
            label="Alterar Senha"
            description="Troque a senha da sua conta"
            onClick={() => setPasswordOpen(true)}
          />
          <ActionRow
            icon={Hash}
            label="Configurar PIN"
            description="Defina ou remova o PIN de acesso rápido"
            onClick={() => setPinOpen(true)}
          />
        </div>

        <ChangePasswordDrawer
          open={passwordOpen}
          onClose={() => setPasswordOpen(false)}
        />
        <PinDrawer open={pinOpen} onClose={() => setPinOpen(false)} />
      </div>
    </>
  );
}
